/**
 * ACT2-690: what chat shows when a question arrives and the workspace has no warehouse.
 * Presentational — {@link ConnectWarehousePrompt} wires the data.
 *
 * Docked in the composer slot, the same place an ask-user card sits, under the waiting question. It lists
 * the warehouse accounts Lookout does not hold yet and can connect, each with the same one-click Connect
 * the Settings → Warehouses page offers. The list is that page's Available rows, from the same
 * `useWarehouseAccountList`, so an account Lookout cannot connect is left out here too, and a sibling
 * workspace (ACT2-1124) lists the accounts of its readable workspaces as well. Creating a new connection
 * opens the Add warehouse drawer instead, which is the only path when there is nothing to adopt.
 * Connecting is admin-only, so "Send anyway" is there for everyone: the agent can still answer questions
 * that need no warehouse.
 */
import type { WarehouseProblem } from '@server/warehouse-failure';
import { Database, Link2 } from 'lucide-react';
import { Link } from 'react-router-dom';

import { ActionTooltip } from '@/_shared/components/ActionTooltip';
import { Button } from '@/_shared/components/ui/button';
import { Callout } from '@/_shared/components/ui/callout';
import { Skeleton } from '@/_shared/components/ui/skeleton';
import { WarehouseProblemCallout } from '@/_shared/components/WarehouseProblemCallout/WarehouseProblemCallout';
import { cn } from '@/_shared/lib/utils';
import type { ConnectableWarehouseAccount } from '@/settings/components/warehouseAccount/useWarehouseAccountList';

import { InteractiveCard } from '../interactive/InteractiveCard';

const NOT_ALLOWED_REASON = 'Ask a RudderStack workspace admin for permission to connect one.';

export interface ConnectWarehouseCardProps {
  /** The question waiting to be sent, shown as the user's bubble above the card. */
  heldContent: string;
  /** Accounts one click of Connect can connect — the Warehouses page's Available rows. */
  accounts: ConnectableWarehouseAccount[];
  /** How many other RudderStack accounts Lookout does not hold but cannot connect. They are not listed. */
  unconnectableCount: number;
  isLoading: boolean;
  /** The RudderStack account list could not be read. */
  unavailable: boolean;
  /** Why it could not be read, when the server said (ACT2-1077): an expired RudderStack link is not a missing warehouse. */
  unavailableProblem?: WarehouseProblem | undefined;
  /** ACT2-1276: only a user RudderStack allows may connect. */
  canConnect: boolean;
  /** Why the user cannot connect, from the RudderStack permission; replaces the generic copy. */
  blockedReason?: string | null;
  /** What the user can do about `blockedReason`: try again, or sign in with RudderStack. */
  blockedAction?: React.ReactNode;
  /** A Connect runs now, so every other action waits. */
  anyConnecting: boolean;
  /** Shown above the accounts, such as the notice that points to Settings › General (ACT2-1124). */
  notice?: React.ReactNode;
  settingsHref: string;
  onConnect: (accountId: string) => void;
  /** Opens the Add warehouse drawer, to create a connection without leaving chat. */
  onCreateNew: () => void;
  /** Sends the question without a warehouse. */
  onSendAnyway: () => void;
  /** Puts the question back in the composer, unsent. */
  onDismiss: () => void;
}

/** One account Lookout can connect, with its Connect button. Also listed by the setup panel (ACT2-1108). */
export function ConnectableAccountRow({
  account,
  canConnect,
  anyConnecting,
  onConnect,
  compact = false,
}: {
  account: ConnectableWarehouseAccount;
  canConnect: boolean;
  anyConnecting: boolean;
  onConnect: () => void;
  /** Smaller icon, padding and button, for a row inside a step that already has a card (the setup panel). */
  compact?: boolean;
}) {
  const { name, detail, error, errorProblem } = account;
  const blocked = canConnect ? null : NOT_ALLOWED_REASON;
  return (
    <li className={cn('rounded-lg hover:bg-muted', compact ? 'px-2 py-1.5' : 'px-2.5 py-2')}>
      <div className={cn('flex items-center', compact ? 'gap-2' : 'gap-3')}>
        <Database aria-hidden className={cn('shrink-0 text-muted-foreground', compact ? 'size-4' : 'size-6')} />
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-sm font-medium text-foreground" title={name}>
            {name}
          </span>
          <span className="text-xs text-muted-foreground">{account.typeLabel}</span>
          {detail !== null && (
            <span className="truncate text-xs text-muted-foreground" title={detail}>
              {detail}
            </span>
          )}
        </span>
        <ActionTooltip show={blocked !== null} message={blocked ?? ''}>
          <Button
            type="button"
            variant="secondary"
            label="Connect"
            size={compact ? 'sm' : 'md'}
            aria-label={account.connectLabel}
            Icon={<Link2 />}
            loading={account.connecting}
            disabled={blocked !== null || anyConnecting}
            onClick={onConnect}
          />
        </ActionTooltip>
      </div>
      {error !== null &&
        (errorProblem ? (
          <div className="mt-2">
            <WarehouseProblemCallout problem={errorProblem} />
          </div>
        ) : (
          <Callout variant="error" className="mt-2">
            {error}
          </Callout>
        ))}
    </li>
  );
}

function HeldQuestion({ content }: { content: string }) {
  return (
    <div className="flex justify-end pb-3">
      <div className="max-w-[85%] animate-slide-up rounded-2xl border border-border bg-card px-4 py-2.5 text-sm whitespace-pre-wrap">
        {content}
      </div>
    </div>
  );
}

export function ConnectWarehouseCard({
  heldContent,
  accounts,
  unconnectableCount,
  isLoading,
  unavailable,
  unavailableProblem,
  canConnect,
  blockedReason = null,
  blockedAction = null,
  anyConnecting,
  notice = null,
  settingsHref,
  onConnect,
  onCreateNew,
  onSendAnyway,
  onDismiss,
}: ConnectWarehouseCardProps): React.ReactElement {
  const nothingToConnect = !isLoading && (unavailable || accounts.length === 0);

  const connecting = anyConnecting;
  const description = !canConnect
    ? `This workspace has no warehouse connected yet. ${blockedReason ?? NOT_ALLOWED_REASON} You can still send your question.`
    : isLoading
      ? 'Looking for warehouse accounts in your RudderStack workspace…'
      : unavailable
        ? null
        : accounts.length > 0
          ? 'Connect one of your RudderStack warehouse accounts and your question is sent right away.'
          : unconnectableCount > 0
            ? 'None of your RudderStack warehouse accounts can be connected from Lookout yet. Set one up here and your question is sent once it connects.'
            : 'Your RudderStack workspace has no warehouse account Lookout can use yet. Set one up here and your question is sent once it connects.';

  return (
    <div>
      <HeldQuestion content={heldContent} />
      <InteractiveCard
        title="Connect a warehouse to answer this"
        ariaLabel="Connect a warehouse"
        busy={connecting}
        onDismiss={onDismiss}
        dismissDisabled={connecting}
        footerLeft={
          // A user who cannot connect has sending as the main action, in the right-hand slot.
          !canConnect ? null : (
            <>
              <Button
                type="button"
                variant="tertiary"
                label="Send anyway"
                disabled={connecting}
                onClick={onSendAnyway}
              />
              {/* A list that failed to load usually needs Settings (an expired RudderStack link), so Open
                  settings is the main action there and setting up a warehouse is the alternative. */}
              {unavailable && !isLoading ? (
                <Button
                  type="button"
                  variant="tertiary"
                  label="Set up a warehouse"
                  disabled={connecting}
                  onClick={onCreateNew}
                />
              ) : nothingToConnect || isLoading ? null : (
                <Button asChild variant="tertiary">
                  <Link to={settingsHref}>Open settings</Link>
                </Button>
              )}
            </>
          )
        }
        footerRight={
          !canConnect ? (
            <Button type="button" variant="primary" label="Send anyway" onClick={onSendAnyway} />
          ) : unavailable && !isLoading ? (
            <Button asChild variant="primary">
              <Link to={settingsHref}>Open settings</Link>
            </Button>
          ) : (
            <Button
              type="button"
              variant={nothingToConnect ? 'primary' : 'secondary'}
              label={nothingToConnect ? 'Set up a warehouse' : 'Create a new connection'}
              disabled={connecting}
              onClick={onCreateNew}
            />
          )
        }
      >
        {description === null && unavailableProblem ? (
          <WarehouseProblemCallout problem={unavailableProblem} />
        ) : description === null ? (
          <Callout variant="warning">
            Your RudderStack warehouse accounts couldn’t be loaded. Set one up here and your question is sent once it
            connects.
          </Callout>
        ) : (
          <p className="px-2.5 py-1 text-sm text-muted-foreground">{description}</p>
        )}
        {!canConnect && blockedAction !== null && <div className="px-2.5 pt-2">{blockedAction}</div>}
        {canConnect && notice !== null && <div className="px-2.5 pt-2">{notice}</div>}
        {isLoading && (
          <div className="flex flex-col gap-2 px-2.5 pt-2" aria-hidden>
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        )}
        {canConnect && !isLoading && !nothingToConnect && (
          <ul className="flex flex-col gap-0.5 pt-1">
            {accounts.map((account) => (
              <ConnectableAccountRow
                key={account.id}
                account={account}
                canConnect={canConnect}
                anyConnecting={connecting}
                onConnect={() => onConnect(account.id)}
              />
            ))}
          </ul>
        )}
      </InteractiveCard>
    </div>
  );
}
